import React, { useMemo } from 'react';
import { ArrowLeft, CreditCard, Edit3, Lock, Plus, Power, PowerOff, RefreshCcw, WalletCards } from 'lucide-react';
import styles from './PrepaidMembershipList.module.css';

const money = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? `₹${amount.toLocaleString('en-IN')}` : '₹0';
};

const getMembershipId = (m) => m?.id || m?._id || m?.templateId;
const getTotalCards = (m) => m?.batchSize ?? m?.cardStats?.total ?? m?.totalCards ?? 0;
const getClientPrice = (m) => m?.clientPays ?? m?.basePrice ?? m?.finance?.basePrice ?? 0;
const getWalletValue = (m) => m?.prepaidCredit ?? m?.walletValue ?? m?.benefits?.walletValue ?? 0;

/**
 * canCreate     -> "Create wallet tier"
 * canEdit       -> "Manage" (price, generate more cards)
 * canDeactivate -> Activate / Deactivate
 * All default to false: nothing shows unless access is given.
 */
export default function PrepaidMembershipList({
    memberships = [], onAddNew, onEdit, onToggleStatus, onBack, onRefresh, isLoading,
    canCreate = false, canEdit = false, canDeactivate = false,
}) {
    const hasRowActions = canEdit || canDeactivate;
    const cols = hasRowActions ? 7 : 6;

    const stats = useMemo(() => memberships.reduce((r, m) => {
        r.tiers += 1;
        r.cards += Number(getTotalCards(m)) || 0;
        return r;
    }, { tiers: 0, cards: 0 }), [memberships]);

    return (
        <div className={styles.listContainer}>
            <div className={styles.header}>
                <div className={styles.headerIdentity}>
                    <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Back">
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <span className={styles.eyebrow}>MEMBERSHIPS / WALLET</span>
                        <h2>Wallet Memberships</h2>
                        <p>{hasRowActions || canCreate ? 'Manage prepaid value plans and generated wallet card inventory.' : 'Prepaid plans and card stock (view only).'}</p>
                    </div>
                </div>

                <div className={styles.headerActions}>
                    <button type="button" className={styles.refreshBtn} onClick={onRefresh} title="Refresh list" aria-label="Refresh list">
                        <RefreshCcw size={14} />
                    </button>
                    {canCreate ? (
                        <button type="button" className={styles.primaryBtn} onClick={onAddNew}>
                            <Plus size={15} /> Create wallet tier
                        </button>
                    ) : (!hasRowActions && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: '#f8f9fa', color: '#797980', fontSize: 11, fontWeight: 800 }}>
                            <Lock size={11} /> View only
                        </span>
                    ))}
                </div>
            </div>

            <div className={styles.statRow}>
                <div className={styles.statCard}>
                    <span className={styles.statIcon}><WalletCards size={16} /></span>
                    <span><strong>{stats.tiers}</strong><small>Total tiers</small></span>
                </div>
                <div className={styles.statCard}>
                    <span className={`${styles.statIcon} ${styles.blueIcon}`}><CreditCard size={16} /></span>
                    <span><strong>{stats.cards}</strong><small>Generated cards</small></span>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Wallet tier</th>
                            <th>Status</th>
                            <th>Card code</th>
                            <th>Client pays</th>
                            <th>Wallet credit</th>
                            <th>Inventory</th>
                            {hasRowActions && <th className={styles.actionColumn}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && Array.from({ length: 4 }).map((_, i) => (
                            <tr key={`loading-${i}`}>
                                <td colSpan={cols}><div className={styles.skeletonRow}><span /><span /><span /><span /></div></td>
                            </tr>
                        ))}

                        {!isLoading && memberships.length === 0 && (
                            <tr>
                                <td colSpan={cols}>
                                    <div className={styles.emptyState}>
                                        <div className={styles.emptyIcon}><CreditCard size={23} /></div>
                                        <strong>No wallet tiers yet</strong>
                                        <span>{canCreate ? 'Create a prepaid wallet tier to generate cards for your salon.' : 'Ask a manager to create prepaid plans.'}</span>
                                        {canCreate && (
                                            <button type="button" className={styles.emptyAction} onClick={onAddNew}>
                                                <Plus size={14} /> Create first tier
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!isLoading && memberships.map((m) => {
                            const id = getMembershipId(m);
                            const isActive = m.status === 'ACTIVE';
                            const muted = isActive ? undefined : '#797980';
                            return (
                                <tr key={id} className={!isActive ? styles.rowInactive : ''}>
                                    <td>
                                        <div className={styles.tierCell}>
                                            <span className={styles.tierIcon} style={{ opacity: isActive ? 1 : 0.6 }}><WalletCards size={15} /></span>
                                            <span>
                                                <strong style={{ color: muted || '#18181a' }}>{m.name || m.customName || 'Wallet tier'}</strong>
                                                <small>{m.validity || 12} {m.validityType || 'Months'}</small>
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={isActive ? styles.statusActive : styles.statusArchived}>
                                            <span className={styles.statusDot} />
                                            {isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td><span className={styles.codeBadge} style={{ opacity: isActive ? 1 : 0.6 }}>{m.cardCode || m.cardTypeObj?.code || '0000'}</span></td>
                                    <td className={styles.moneyCell} style={{ color: muted }}>{money(getClientPrice(m))}</td>
                                    <td className={`${styles.moneyCell} ${styles.creditCell}`} style={{ color: muted || 'var(--h-color-primary-600)' }}>{money(getWalletValue(m))}</td>
                                    <td>
                                        <div className={styles.inventoryCell} style={{ opacity: isActive ? 1 : 0.5 }}>
                                            <strong>{getTotalCards(m)}</strong><small>cards</small>
                                        </div>
                                    </td>
                                    {hasRowActions && (
                                        <td className={styles.actionColumn}>
                                            <div className={styles.actionGroup}>
                                                {canEdit && (
                                                    <button type="button" className={styles.actionBtn} onClick={() => onEdit(m)}>
                                                        <Edit3 size={13} /> Manage
                                                    </button>
                                                )}
                                                {canDeactivate && (
                                                    <button
                                                        type="button"
                                                        className={isActive ? styles.actionBtnDanger : styles.actionBtnSuccess}
                                                        onClick={() => {
                                                            if (window.confirm(isActive ? `Stop "${m.name}"? No new cards can be printed or sold. Cards already with clients keep working.` : `Start "${m.name}" again?`)) onToggleStatus(id);
                                                        }}
                                                        title={isActive ? 'Stop new cards' : 'Start again'}
                                                    >
                                                        {isActive ? <PowerOff size={13} /> : <Power size={13} />}
                                                        {isActive ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}